import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    FiArrowLeft,
    FiCheckCircle,
    FiFileText,
    FiShield,
    FiClock,
    FiTruck,
    FiPhoneCall,
    FiChevronDown,
    FiHelpCircle,
    FiSliders,
    FiPercent,
    FiCalendar,
    FiMapPin
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import FeaturedProductsSection from '../components/common/FeaturedProductsSection';
import { calculateInstallment, is50PercentCategory } from '../utils/installmentCalculator';
import { formatPriceDisplay, formatNumberPKR } from '../utils/cashPricing';
import { BUSINESS_WHATSAPP_CHAT_URL, BUSINESS_PHONE_MAIN } from '../config/businessInfo';
import './InstallmentPage.css';

const SHOWROOM_IMG_1 = '/assets/media/images/shop/IMG_5702.HEIC_20261022225000.jpg';

const FAQ_ITEMS = [
    {
        q: 'What documents are required to buy on installment?',
        a: '• CNIC copy & recent electricity/gas bill copy\n• 2 personal references (family, colleague, or neighbor)\n• Easy Verification: Visit our Lahore showroom or request home verification (Rs. 1,000 – Rs. 2,000 visiting charges apply)\n• Note: Cheques are not mandatory, only requested if needed.'
    },
    {
        q: 'Is there any hidden markup or file processing fee?',
        a: 'Absolutely not. At Rahber Corporation, transparency has been our hallmark since 1980. Every rupee is stated clearly upfront before you sign or pay your advance. There are no sudden extra document or service charges.'
    },
    {
        q: 'Can I pay my installments early or in advance?',
        a: 'Yes, you can pay off your installments early or clear your full balance ahead of time! However, please note that we do not offer any discount for early payoff because our plans strictly follow Islamic financing principles. In Islam, once a contract and total sale price are agreed upon, the terms cannot be altered. Offering discounts for early settlement would fall under interest (Riba), so the agreed contract price remains fixed, transparent, and Shariah-compliant.'
    },
    {
        q: 'How are monthly installments collected?',
        a: 'You can pay easily via online bank transfer, Raast, JazzCash, EasyPaisa, or visit our Lahore showroom in person. For local customers, our authorized field agent can also collect directly with an official stamped receipt.'
    },
    {
        q: 'Do installment products have genuine company warranties?',
        a: 'Yes, 100%! All appliances sold on installment come brand-new in original factory-sealed boxes with official manufacturer warranty cards.\n\n• Official 18 Partner Brands: Dawlance, PEL, Orient, Multynet, Konvo, TCL, Gree, Eco Star, United, Care Appliances, Super Asia, Waves, Enviro, Samsung, Boss Appliances, Sabro Appliances, izone, and F&D Speakers.\n• Warranty Responsibility: Please note that all warranty claims, after-sales service, compressor replacements, and repairs are the direct legal responsibility of the respective manufacturing brand companies and their official service centers across Lahore and Pakistan, not the retail showroom directly. We provide you with the stamped company warranty card so you can claim service directly from official brand technicians.'
    }
];

const PRESETS = [65000, 110000, 185000, 260000, 380000];

const InstallmentPage = () => {
    const navigate = useNavigate();

    // Calculator State
    const [categoryType, setCategoryType] = useState('appliances'); // 'appliances' | 'mobiles' | 'bikes'
    const [price, setPrice] = useState(120000);
    const [tenure, setTenure] = useState(12); // 6, 9, 12, 4
    const [downPercent, setDownPercent] = useState(25); // 25, 50, 75
    const [openFaq, setOpenFaq] = useState(0);

    const isSpecial50 = is50PercentCategory(categoryType);
    const effectiveDown = isSpecial50 ? 50 : downPercent;

    // Exact formula matching Rahber Corporation Excel rate-list
    const calculation = useMemo(() => {
        const result = calculateInstallment({
            mrp: price,
            months: tenure,
            downPaymentPercent: effectiveDown,
            category: categoryType,
        });

        if (result.ok) {
            return {
                ...result,
                effectiveDown,
                isSpecial50,
            };
        }

        const advance = Math.round((price * effectiveDown) / 100);
        return {
            advance,
            downPaymentAmount: advance,
            effectiveDown,
            isSpecial50,
            amountAfterDown: price - advance,
            monthlyInstallment: Math.round((price - advance) / tenure),
            totalPayable: price,
            profitAmount: 0,
            markupRate: isSpecial50 ? 50 : 45,
        };
    }, [price, tenure, effectiveDown, categoryType, isSpecial50]);

    const handleWhatsappInquiry = () => {
        const catLabel =
            categoryType === 'mobiles'
                ? 'Mobile / Smartphone (50% Advance • 50% Markup)'
                : categoryType === 'bikes'
                ? 'Bike / Motorcycle (50% Advance • 50% Markup)'
                : 'Home Appliance (45% Markup)';
        const text = `Hello Rahber Corporation! I am interested in an installment plan for a ${catLabel} valued at ${formatPriceDisplay(price)}. 
Plan chosen:
- Category: ${catLabel}
- Duration: ${tenure} Months
- Advance (${calculation.effectiveDown}%): ${formatPriceDisplay(calculation.downPaymentAmount)}
- Monthly Installment: ${formatPriceDisplay(calculation.monthlyInstallment)}
- Total Payable: ${formatPriceDisplay(calculation.totalPayable)}
Please guide me on the next verification steps.`;
        window.open(`${BUSINESS_WHATSAPP_CHAT_URL}?text=${encodeURIComponent(text)}`, '_blank');
    };

    return (
        <div className="installment-page">
            {/* Rich Hero Header with Integrated Back Navigation & Trust Badges */}
            <header className="installment-hero-premium">
                <div className="container">
                    <div style={{ marginBottom: '1.25rem', textAlign: 'left' }}>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="product-page-back-btn"
                            aria-label="Go back"
                        >
                            <FiArrowLeft /> Back
                        </button>
                    </div>

                    <div className="installment-hero-badge">
                        <FiShield /> Transparent &amp; Flexible Appliance Installments
                    </div>
                    <h1 className="installment-hero-title">
                        Buy Appliances on <span className="highlight-text">Easy Monthly Installments</span>
                    </h1>
                    <p className="installment-hero-desc">
                        Upgrade your home with Dawlance, PEL, Orient, TCL, Gree, and Waves appliances. Choose convenient 4 to 12 month payment tenures with straightforward verification and safe doorstep delivery in Lahore.
                    </p>

                    <div className="installment-trust-strip">
                        <div className="trust-strip-item">
                            <span className="strip-value">24 Working Hours</span>
                            <span className="strip-label">Prompt Verification</span>
                        </div>
                        <div className="trust-strip-divider" />
                        <div className="trust-strip-item">
                            <span className="strip-value">4 to 12 Mo (4 Mo 0%)</span>
                            <span className="strip-label">Customizable Tenures</span>
                        </div>
                        <div className="trust-strip-divider" />
                        <div className="trust-strip-item">
                            <span className="strip-value">Direct Rates</span>
                            <span className="strip-label">Clear Written Terms</span>
                        </div>
                        <div className="trust-strip-divider" />
                        <div className="trust-strip-item">
                            <span className="strip-value">Doorstep Handover</span>
                            <span className="strip-label">Lahore &amp; Nearby Delivery</span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Interactive Calculator & Visual Breakdown */}
            <section className="installment-calc-section">
                <div className="container">
                    <div className="section-header text-center">
                        <span className="section-eyebrow">INSTANT ESTIMATE</span>
                        <h2 className="section-title">Interactive Installment Calculator</h2>
                        <p className="section-subtitle">
                            Estimate your down payment and monthly budget in real time before choosing your appliance.
                        </p>
                    </div>

                    <div className="calc-master-card">
                        <div className="calc-controls-col">
                            {/* Category Selector with Excel formula markup rates */}
                            <div className="calc-field-group">
                                <label className="calc-label">
                                    <FiSliders /> Select Product Category
                                </label>
                                <div className="calc-pill-selector">
                                    {[
                                        { id: 'appliances', label: 'Home Appliances', badge: '45% Markup • 25%-75% Adv' },
                                        { id: 'bikes', label: 'Bikes & EV Bikes', badge: '50% Markup • 50% Adv' },
                                        { id: 'mobiles', label: 'Mobiles & Tablets', badge: '50% Markup • 50% Adv' },
                                    ].map((cat) => (
                                        <button
                                            key={cat.id}
                                            type="button"
                                            onClick={() => {
                                                setCategoryType(cat.id);
                                                if (cat.id === 'bikes' || cat.id === 'mobiles') {
                                                    setDownPercent(50);
                                                }
                                            }}
                                            className={`calc-pill ${categoryType === cat.id ? 'active' : ''}`}
                                        >
                                            <span style={{ display: 'block', fontWeight: 800 }}>{cat.label}</span>
                                            <span style={{ display: 'block', fontSize: '0.72rem', opacity: 0.85, marginTop: '2px' }}>
                                                {cat.badge}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Price Range Slider */}
                            <div className="calc-field-group">
                                <div className="calc-field-header">
                                    <label htmlFor="priceSlider" className="calc-label">
                                        <FiSliders /> Product Price / Retail MRP (PKR)
                                    </label>
                                    <span className="calc-price-display">
                                        {formatPriceDisplay(price)}
                                    </span>
                                </div>
                                <input
                                    id="priceSlider"
                                    type="range"
                                    min="30000"
                                    max="500000"
                                    step="5000"
                                    value={price}
                                    onChange={(e) => setPrice(Number(e.target.value))}
                                    className="calc-range-slider"
                                />
                                <div className="calc-presets">
                                    <span className="presets-label">Popular Price Points:</span>
                                    {PRESETS.map((p) => (
                                        <button
                                            key={p}
                                            type="button"
                                            onClick={() => setPrice(p)}
                                            className={`preset-btn ${price === p ? 'active' : ''}`}
                                        >
                                            {p / 1000}k
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Plan Duration / Tenure */}
                            <div className="calc-field-group">
                                <label className="calc-label">
                                    <FiCalendar /> Plan Duration (Tenure)
                                </label>
                                <div className="calc-pill-selector">
                                    {[4, 6, 9, 12].map((m) => (
                                        <button
                                            key={m}
                                            type="button"
                                            onClick={() => setTenure(m)}
                                            className={`calc-pill ${tenure === m ? 'active' : ''}`}
                                        >
                                            <span style={{ display: 'block', fontWeight: 800 }}>{m} Months</span>
                                            <span style={{ display: 'block', fontSize: '0.72rem', opacity: 0.85, marginTop: '2px' }}>
                                                {m === 4 ? '0% Markup' : m === 12 ? 'Standard 12M' : `${m}-Month Plan`}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Down Payment Percentage */}
                            <div className="calc-field-group">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label className="calc-label">
                                        <FiPercent /> Down Payment (Advance)
                                    </label>
                                    {isSpecial50 && (
                                        <span style={{ fontSize: '0.75rem', color: 'var(--accent-orange, #EA580C)', fontWeight: 700 }}>
                                            50% Fixed Advance Policy Applies
                                        </span>
                                    )}
                                </div>
                                <div className="calc-pill-selector">
                                    {(isSpecial50 ? [50] : [25, 50, 75]).map((pct) => (
                                        <button
                                            key={pct}
                                            type="button"
                                            onClick={() => setDownPercent(pct)}
                                            className={`calc-pill ${effectiveDown === pct ? 'active' : ''}`}
                                        >
                                            <span style={{ display: 'block', fontWeight: 800 }}>{pct}% Advance</span>
                                            <span style={{ display: 'block', fontSize: '0.72rem', opacity: 0.85, marginTop: '2px' }}>
                                                {formatPriceDisplay(Math.round((price * pct) / 100))}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right Output Panel */}
                        <div className="calc-summary-col">
                            <div className="calc-summary-card">
                                <div className="summary-card-top">
                                    <span className="summary-eyebrow">YOUR ESTIMATED MONTHLY PAYMENT</span>
                                    <div className="summary-monthly-figure">
                                        <span className="currency">Rs.</span>
                                        <span className="amount">{formatNumberPKR(calculation.monthlyInstallment)}</span>
                                        <span className="per-month">/ month</span>
                                    </div>
                                    <p className="summary-period-note">
                                        Payable in {tenure} equal monthly installments
                                    </p>
                                </div>

                                <div className="summary-breakdown-list">
                                    <div className="breakdown-row">
                                        <span>Advance Payment ({calculation.effectiveDown}%)</span>
                                        <strong>{formatPriceDisplay(calculation.downPaymentAmount)}</strong>
                                    </div>
                                    <div className="breakdown-row">
                                        <span>Product Retail MRP</span>
                                        <span>{formatPriceDisplay(price)}</span>
                                    </div>
                                    <div className="breakdown-row">
                                        <span>Financed Balance</span>
                                        <span>{formatPriceDisplay(calculation.amountAfterDown)}</span>
                                    </div>
                                    <div className="breakdown-row">
                                        <span>Installment Markup ({calculation.markupRate}%)</span>
                                        <span style={{ color: '#FDBA74', fontWeight: 600 }}>
                                            + {formatPriceDisplay(calculation.profitAmount)}
                                        </span>
                                    </div>
                                    <div className="summary-total-banner">
                                        <div className="total-banner-left">
                                            <span className="total-banner-label">Total Payable Amount</span>
                                            <span className="total-banner-tenure">({tenure} Mo Plan)</span>
                                        </div>
                                        <div className="total-banner-price">
                                            <span className="total-currency-badge">Rs.</span>
                                            <span className="total-price-amount">{formatNumberPKR(calculation.totalPayable)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="summary-cta-group">
                                    <button
                                         type="button"
                                         onClick={handleWhatsappInquiry}
                                         className="btn-calc-whatsapp"
                                     >
                                         <FaWhatsapp /> Inquire This Exact Plan
                                     </button>
                                     <Link to="/products" className="btn-calc-browse">
                                         Browse Products In This Range
                                     </Link>
                                 </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Visual 4-Step Process Section */}
            <section className="installment-journey-section">
                <div className="container">
                    <div className="section-header text-center">
                        <span className="section-eyebrow">TRANSPARENT JOURNEY</span>
                        <h2 className="section-title">How Our Installment Process Works</h2>
                        <p className="section-subtitle">
                            4 simple steps to receive your genuine appliance at your doorstep with zero complications.
                        </p>
                    </div>

                    <div className="journey-grid">
                        <div className="journey-card">
                            <div className="journey-step-num">01</div>
                            <div className="journey-icon-wrap">
                                <FiFileText />
                            </div>
                            <h3>Choose Your Appliance</h3>
                            <p>
                                Explore our inverter ACs, refrigerators, LED TVs, and deep freezers. Select the model that matches your family&apos;s cooling and capacity needs.
                            </p>
                            <div className="journey-footer">
                                <Link to="/products" className="journey-link">Explore catalog &rarr;</Link>
                            </div>
                        </div>

                        <div className="journey-card">
                            <div className="journey-step-num">02</div>
                            <div className="journey-icon-wrap">
                                <FiSliders />
                            </div>
                            <h3>Select Your Custom Plan</h3>
                            <p>
                                On each product page, view exact calculations for 4, 6, 9, or 12 months. Choose the advance amount that fits your monthly salary or budget.
                            </p>
                            <div className="journey-footer">
                                <span className="journey-tag">No hidden charges</span>
                            </div>
                        </div>

                        <div className="journey-card">
                            <div className="journey-step-num">03</div>
                            <div className="journey-icon-wrap">
                                <FiClock />
                            </div>
                            <h3>Quick 24-Hour Clearance</h3>
                            <p>
                                Submit basic CNIC and utility bill copies. Our Lahore team completes verification within 24 hours with polite, professional customer service.
                            </p>
                            <div className="journey-footer">
                                <span className="journey-tag">Fast &amp; Respectful</span>
                            </div>
                        </div>

                        <div className="journey-card">
                            <div className="journey-step-num">04</div>
                            <div className="journey-icon-wrap">
                                <FiTruck />
                            </div>
                            <h3>Doorstep Delivery &amp; Setup</h3>
                            <p>
                                We deliver the sealed box directly to your address in Lahore. Unbox together, verify warranty card, and pay advance.
                            </p>
                            <div className="journey-footer">
                                <span className="journey-tag">Genuine Warranty</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Verification Documents & Eligibility Showcase */}
            <section className="installment-docs-section">
                <div className="container">
                    <div className="docs-dual-card">
                        <div className="docs-text-col">
                            <span className="section-eyebrow">SIMPLE ELIGIBILITY</span>
                            <h2>What Do You Need to Apply?</h2>
                            <p className="docs-lead">
                                We believe in building lasting relationships with families and businesses. Our documentation process is designed to be accessible and straightforward.
                            </p>

                            <div className="docs-checklist">
                                <div className="doc-check-item">
                                    <FiCheckCircle className="check-icon" />
                                    <div>
                                        <strong>Photocopy of Valid CNIC</strong>
                                        <p>Front and back copy of the applicant&apos;s national identity card.</p>
                                    </div>
                                </div>

                                <div className="doc-check-item">
                                    <FiCheckCircle className="check-icon" />
                                    <div>
                                        <strong>Latest Utility Bill Copy</strong>
                                        <p>Recent paid electricity, gas, or PTCL bill for home address verification.</p>
                                    </div>
                                </div>

                                <div className="doc-check-item">
                                    <FiCheckCircle className="check-icon" />
                                    <div>
                                        <strong>Two Personal References</strong>
                                        <p>Contact numbers and CNIC numbers of 2 references (relatives, colleagues, or neighbors).</p>
                                    </div>
                                </div>

                                <div className="doc-check-item">
                                    <FiCheckCircle className="check-icon" />
                                    <div>
                                        <strong>Proof of Income / Employment</strong>
                                        <p>Salary slip, business visiting card, shop registration, or bank statement.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="docs-cta-banner">
                                <div className="docs-cta-phone">
                                    <FiPhoneCall className="docs-phone-icon" />
                                    <div>
                                        <span className="docs-phone-label">Have questions about eligibility?</span>
                                        <strong className="docs-phone-numbers">{BUSINESS_PHONE_MAIN[0].display} / {BUSINESS_PHONE_MAIN[1].display}</strong>
                                    </div>
                                </div>
                                <a
                                    href={BUSINESS_WHATSAPP_CHAT_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="docs-chat-cta-btn"
                                >
                                    <FaWhatsapp /> Chat with Verification Team
                                </a>
                            </div>
                        </div>

                        {/* Right Showroom Media Proof */}
                        <div className="docs-media-col">
                            <div className="docs-image-wrapper">
                                <img
                                    src={SHOWROOM_IMG_1}
                                    alt="Rahber Corporation Showroom Lahore"
                                    className="docs-main-img"
                                    loading="lazy"
                                />
                                <div className="docs-elegant-caption">
                                    <FiMapPin className="docs-caption-pin" aria-hidden="true" />
                                    <span>Rahber Corporation Flagship Showroom • Sanda Kalan, Lahore</span>
                                </div>
                            </div>
                            <div className="docs-sub-quote">
                                <blockquote>
                                    &ldquo;Installments should give families peace of mind, not stress. That is why our family built Rahber Corporation on trust, fair terms, and respect.&rdquo;
                                </blockquote>
                                <cite>Showroom Customer Desk, Rahber Corporation</cite>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Installment Products Grid */}
            <section className="installment-products-section">
                <div className="container">
                    <FeaturedProductsSection
                        channel="installment"
                        title="Top Appliances Available on Installment"
                        subtitle="All items below are in-stock at our Lahore showroom and eligible for 3 to 12 months plans."
                    />
                </div>
            </section>

            {/* Comprehensive Installment FAQ Accordion */}
            <section className="installment-faq-section">
                <div className="container">
                    <div className="section-header text-center">
                        <span className="section-eyebrow">COMMON QUESTIONS</span>
                        <h2 className="section-title">Installment Plan FAQs</h2>
                        <p className="section-subtitle">
                            Clear answers to everything you need to know about down payments, tenures, and delivery.
                        </p>
                    </div>

                    <div className="faq-accordion-container">
                        {FAQ_ITEMS.map((item, idx) => {
                            const isOpen = openFaq === idx;
                            return (
                                <div
                                    key={item.q}
                                    className={`faq-accordion-item ${isOpen ? 'active' : ''}`}
                                >
                                    <button
                                        type="button"
                                        className="faq-accordion-header"
                                        onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                                        aria-expanded={isOpen}
                                    >
                                        <span className="faq-question">
                                            <FiHelpCircle className="faq-q-icon" />
                                            {item.q}
                                        </span>
                                        <FiChevronDown className={`faq-chevron ${isOpen ? 'rotate' : ''}`} />
                                    </button>
                                    {isOpen && (
                                        <div className="faq-accordion-body">
                                            <p>{item.a}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default InstallmentPage;
